// Idle-time photo prefetch for Collections.
//
// After the service worker cache-firsts species photos on the two iNat CDNs
// and Wikimedia (sw.js), the first render of Collections is still bounded
// by network — those photos have never been requested at all until the
// user opens the tab. Warming them from Home closes that gap: by the time
// the user taps the Collections icon, the top-N thumbs are already resident
// in the SW cache.
//
// Uses requestIdleCallback so nothing competes with what the user is
// actually doing on Home. Bandwidth-conscious by default: obeys the
// `Save-Data` header on the connection (returns without a request) so
// users on a metered connection or Low Data Mode don't pay for something
// they haven't opted into.

import { sizedPhotoUrl } from "./thumb";

interface Prefetchable {
  photo_url?: string | null;
  scientific_name?: string | null;
}

interface ConnectionInfo {
  saveData?: boolean;
  effectiveType?: string;
}
interface NavigatorWithConn extends Navigator {
  connection?: ConnectionInfo;
}

const seen = new Set<string>();

function shouldSkip(): boolean {
  if (typeof navigator === "undefined") return true;
  const conn = (navigator as NavigatorWithConn).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  // 2g / slow-2g: also skip. 3g and above are fine.
  const eff = conn.effectiveType ?? "";
  return eff === "slow-2g" || eff === "2g";
}

function scheduleIdle(cb: () => void): void {
  if (typeof window === "undefined") return;
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (ric) ric(cb, { timeout: 2000 });
  else setTimeout(cb, 600);
}

/**
 * Warm the SW image cache for the top-N most-recent lifelist entries.
 * Idempotent — each URL is only ever requested once per session even if
 * called on every render. Safe to call unconditionally; no-ops on Save-Data
 * connections and inside SSR.
 */
export function prefetchLifelistPhotos(
  entries: Prefetchable[],
  limit = 12,
): void {
  if (typeof window === "undefined") return;
  if (shouldSkip()) return;

  scheduleIdle(() => {
    let requested = 0;
    for (const e of entries) {
      if (requested >= limit) break;
      const url = e.photo_url ?? "";
      if (!url) continue;
      // Ask the CDN for the same size Collections' Tile actually renders
      // (~88 px width), so the SW caches the exact URL the img tag will
      // request. Otherwise we'd warm a URL nobody hits.
      const sized = sizedPhotoUrl(url, "small");
      if (seen.has(sized)) continue;
      seen.add(sized);
      // Fire-and-forget: the SW's fetch listener catches this, caches the
      // response, and there's nothing for us to do with the result. Use
      // Image() rather than fetch() so we get browser-native image
      // prioritisation (below anything user-visible).
      const img = new Image();
      img.decoding = "async";
      img.loading = "lazy";
      img.src = sized;
      requested += 1;
    }
  });
}
